import { z } from 'zod/v4';

import {
    hoverEffectsSchemaLegacy,
    menuItemSchemaLegacy,
    narrowWidgetPositionSchemaLegacy,
    selectedFileSchemaLegacy,
} from '../../../common/schemas/legacy';

/**
 * Data shape for the `menu` widget — the drop-anywhere sibling of `navigation`, with the same
 * menu-list/typography/layout fields plus an embedded, optional logo (`isLogo` +
 * `uploadImage`/`logoPosition`/`logoWidth`) that duplicates the standalone `logo` widget with its
 * own fields rather than referencing it.
 *
 * **`fontStile`/`submenuFontStile` are misspelled, on purpose — this is the real persisted field
 * name** (`menu-widget.type.ts`), not a typo introduced here. `navigation`'s equivalent
 * (`navigationLegacyDataSchema`) spells it correctly, `fontStyle`/`submenuFontStyle`. Two
 * different widgets, two different spellings; kept verbatim so this schema parses real data.
 */
export const menuLegacyDataSchema = z.object({
    isLogo: z.boolean(),
    logoWidth: z.number().nullish(),
    uploadImage: selectedFileSchemaLegacy.nullable(),
    logoPosition: z.enum(['Left', 'Center', 'Right']),
    menuList: z.array(menuItemSchemaLegacy),
    textColor: z.string(),
    submenuTextColor: z.string().nullish(),
    themeTextColor: z.string().nullable(),
    fontSize: z.number(),
    submenuFontSize: z.number().nullish(),
    menuItemIndent: z.enum(['S', 'M', 'L']),
    horizontalPosition: z.enum(['L', 'M', 'R']),
    fontStile: z.array(z.enum(['I', 'B', 'AT'])),
    submenuFontStile: z.array(z.enum(['I', 'B', 'AT'])).nullish(),
    submenuListBackground: z.string().nullish(),
    layoutDirection: z.enum(['Horizontal', 'Vertical']),
    showIconOnMobile: z.boolean(),
    iconColor: z.string(),
    iconPosition: z.enum(['L', 'M', 'R']),
    iconSize: z.enum(['S', 'M', 'L']),
    hoverEffects: hoverEffectsSchemaLegacy,
    isSynchronizeStyle: z.boolean(),
    hideOnMobile: z.boolean().nullish(),
    hideOnTablet: z.boolean().nullish(),
    hideOnDesktop: z.boolean().nullish(),
    // Narrower than the shared "simple" grid button/others use — no tablet* fields, no
    // mobileWidth. `menu-widget.type.ts`'s `MenuWidgetData['position']` genuinely has only nine
    // fields; `MenuControlComponent`'s FormGroup declares the missing tablet controls anyway
    // (dead code — see `narrowWidgetPositionSchemaLegacy`'s own doc). Shared with `accordion`,
    // which turned out to have the byte-identical shape.
    position: narrowWidgetPositionSchemaLegacy.nullish(),
});
